// @ts-ignore
import { match, History, NavigateFn } from '@reach/router';
import type { HistoryLocation } from '@reach/router';
import { computed, makeObservable, observable, runInAction } from 'mobx';

import type { NavLink } from '@type/NavLink';
import { isSSR } from '@utils/isSSR';
import { insertParams } from './utils';

export type NavigationLink = {
  href: string;
  run: (options?: RunOptions) => void;
  createLink: (props?: RunOptions) => NavLink;
};

const DOCUSIGN_EVENT_PARAM = 'event';
const DOCUSIGN_RETURN_PARAM = 'docusign';

type MatchResult = null | { uri: string; path: string; [param: string]: string };

export interface NavigationService {
  openedUserCardIdOnPortfolio: number | undefined;
  openedUserCardIdOnOffer: { cardId: number; ideaId: number } | undefined;
  createUserCard: number | undefined;
  userCardOnDocsPage: number;
  toUserCard(id: number): NavigationLink;
  toCreateCard(ideaId: number): NavigationLink;
  toUserCardOnIdeaPage(ideaId: number, cardId: number): NavigationLink;
  toPortfolio(): NavigationLink;
  toOffer(id: number): NavigationLink;
  toOffers(): NavigationLink;
  toHIW(): NavigationLink;
  toHIWPost(id: number): NavigationLink;
  toKYCStep1(): NavigationLink;
  toKYCStep2(): NavigationLink;
  matchKYCStep2: MatchResult;
  currentLocation: HistoryLocation;
  navigate: NavigateFn;
  matchKycStep1: MatchResult;
  toProfile(): NavigationLink;
  makeReturnUrlToDocusign(cardId: number): string;
  toExternalLink(href: string): void;
  isReturnedFromDocusign: boolean;
  removeDocusignReturnParam(): void;
}

interface RunOptions {
  replace?: boolean;
  openNewTab?: boolean;
  openWithReload?: boolean;
}

export const PATH_PATTERNS = {
  USER_CARD_IDEA_SCREEN: '/offerings/:ideaId/card/:cardId',
  CREATE_IDEA_CARD: '/offerings/:ideaId/create-card',
  OFFER_SCREEN: '/offerings/:ideaId',
  OFFERS_SCREEN: '/offerings',
  PORTFOLIO: '/portfolio',
  USER_CARD: '/portfolio/:id',
  PROFILE: '/profile/',
  KYC_STEP_1: '/profile/kyc/',
  SIGN_DOCUMENTS: '/sign-documents/:id',
  SELF_CERTIFICATION: '/profile/investors-status-verification',
  HIW: '/app-guide/',
  HIW_POST: '/app-guide/:id'
};

type ShouldUpdateScrollArgs = {
  prevRouterProps?: {
    location: Location;
  };
  pathname: string;
  routerProps: {
    location: Location;
  };
};

export function shouldUpdateScroll(args: ShouldUpdateScrollArgs) {
  const prevPathname = args.prevRouterProps?.location.pathname || '';
  // Юзер переходит со страницы с идеями на модалки карточек
  if (
    match(PATH_PATTERNS.OFFER_SCREEN, prevPathname) &&
    (match(PATH_PATTERNS.USER_CARD_IDEA_SCREEN, args.pathname) ||
      match(PATH_PATTERNS.CREATE_IDEA_CARD, args.pathname))
  ) {
    return false;
  }

  // Юзер переходит со модалок карточек на идеи
  if (
    match(PATH_PATTERNS.OFFER_SCREEN, args.pathname) &&
    (match(PATH_PATTERNS.USER_CARD_IDEA_SCREEN, prevPathname) ||
      match(PATH_PATTERNS.CREATE_IDEA_CARD, prevPathname))
  ) {
    return false;
  }

  // Юзер переходит из порфолио на модалку карточки
  if (
    match(PATH_PATTERNS.USER_CARD, args.pathname) &&
    match(PATH_PATTERNS.PORTFOLIO, prevPathname)
  ) {
    return false;
  }

  // Юзер переходит из карточки в портфолио
  if (
    match(PATH_PATTERNS.PORTFOLIO, args.pathname) &&
    match(PATH_PATTERNS.USER_CARD, prevPathname)
  ) {
    return false;
  }

  const parsedQuery = new URLSearchParams(args.routerProps.location.search);
  const prevParsedQuery = new URLSearchParams(args.prevRouterProps?.location.search);

  // Авторизация
  if (parsedQuery.get('modalScreen') || prevParsedQuery.get('modalScreen')) {
    return false;
  }

  return true;
}

export class NavigationServiceImpl implements NavigationService {
  @observable
  public currentLocation: HistoryLocation = this.history.location;

  private go(href: string, options?: RunOptions) {
    if (options?.replace) {
      this.navigate(href, { replace: true });
    } else if (options?.openWithReload) {
      this.location.href = href;
    } else if (options?.openNewTab) {
      this.window.open(href);
    } else {
      this.navigate(href);
    }
  }

  generateLink(href: string, defOptions?: RunOptions): NavigationLink {
    return {
      href,
      run: (options = defOptions) => this.go(href, options),
      createLink: (props = defOptions) => {
        return {
          href,
          target: props?.openNewTab ? '_blank' : undefined,
          onClick: (e?: React.SyntheticEvent) => {
            e?.preventDefault();
            this.go(href, props);
          }
        };
      }
    };
  }

  @computed
  get openedUserCardIdOnPortfolio(): number | undefined {
    if (isSSR()) {
      return undefined;
    }
    const result = match(PATH_PATTERNS.USER_CARD, this.currentLocation.pathname);

    return result?.params.id;
  }

  @computed
  get openedUserCardIdOnOffer() {
    if (isSSR()) {
      return undefined;
    }
    const result = match(PATH_PATTERNS.USER_CARD_IDEA_SCREEN, this.currentLocation.pathname);
    if (result) {
      return {
        ideaId: result.params.ideaId,
        cardId: result.params.cardId
      };
    }
    return undefined;
  }

  @computed
  get userCardOnDocsPage() {
    const result = match(PATH_PATTERNS.SIGN_DOCUMENTS, this.currentLocation.pathname);
    return +result.params.id;
  }

  get createUserCard(): number | undefined {
    if (isSSR()) {
      return undefined;
    }
    const result = match(PATH_PATTERNS.CREATE_IDEA_CARD, this.currentLocation.pathname);
    return result?.params.ideaId;
  }

  toUserCard = (id: number, options?: RunOptions) => {
    return this.generateLink(insertParams(PATH_PATTERNS.USER_CARD, { id }), options);
  };

  toCreateCard = (ideaId: number, options?: RunOptions) => {
    return this.generateLink(insertParams(PATH_PATTERNS.CREATE_IDEA_CARD, { ideaId }), options);
  };

  toUserCardOnIdeaPage = (ideaId: number, cardId: number, options?: RunOptions) => {
    return this.generateLink(
      insertParams(PATH_PATTERNS.USER_CARD_IDEA_SCREEN, { ideaId, cardId }),
      options
    );
  };

  toPortfolio = (options?: RunOptions) => {
    return this.generateLink(PATH_PATTERNS.PORTFOLIO, options);
  };

  toOffer = (id: number, options?: RunOptions): NavigationLink => {
    return this.generateLink(insertParams(PATH_PATTERNS.OFFER_SCREEN, { ideaId: id }), options);
  };

  toOffers = (options?: RunOptions): NavigationLink => {
    return this.generateLink(PATH_PATTERNS.OFFERS_SCREEN, options);
  };

  toProfile = (options?: RunOptions): NavigationLink => {
    return this.generateLink(PATH_PATTERNS.PROFILE, options);
  };

  toKYCStep1 = (options?: RunOptions): NavigationLink => {
    return this.generateLink(PATH_PATTERNS.KYC_STEP_1, options);
  };

  toHIW = (options?: RunOptions): NavigationLink => {
    return this.generateLink(PATH_PATTERNS.HIW, options);
  };

  toHIWPost = (id: number, options?: RunOptions): NavigationLink => {
    return this.generateLink(insertParams(PATH_PATTERNS.HIW_POST, { id }), options);
  };

  @computed
  get matchKycStep1(): MatchResult {
    if (isSSR()) {
      return null;
    }

    return match(PATH_PATTERNS.KYC_STEP_1, this.currentLocation.pathname);
  }

  toKYCStep2(): NavigationLink {
    return this.generateLink(PATH_PATTERNS.SELF_CERTIFICATION);
  }

  @computed
  get matchKYCStep2() {
    if (isSSR()) {
      return null;
    }

    return match(PATH_PATTERNS.SELF_CERTIFICATION, this.location.pathname);
  }

  makeReturnUrlToDocusign(cardId: number) {
    const { href } = this.toUserCard(cardId);
    const returnUrl = new URL(href, this.location.href);
    returnUrl.searchParams.append(DOCUSIGN_RETURN_PARAM, 'true');

    return returnUrl.toString();
  }

  get isReturnedFromDocusign() {
    return new URL(this.location.href).searchParams.has(DOCUSIGN_RETURN_PARAM);
  }

  removeDocusignReturnParam = () => {
    const url = new URL(this.location.href);
    url.searchParams.delete(DOCUSIGN_RETURN_PARAM);
    url.searchParams.delete(DOCUSIGN_EVENT_PARAM);

    this.navigate(url.toString());
  };

  toExternalLink(href: string) {
    this.location.href = href;
  }

  constructor(
    private location: Location,
    private window: Window,
    private history: History,
    public navigate: NavigateFn
  ) {
    makeObservable(this);
    history.listen(({ location: newLocation }) => {
      runInAction(() => {
        this.currentLocation = newLocation;
      });
    });
  }
}
